import { apiService } from "api/apiService";
import { toAxiosUploadProgress } from "shared/utils/axiosProgress";
import useHelpers from "composables/useHelpers.js";
import { remove as _remove } from "lodash";
import { SuccessCriteria } from "models/learning-objectives/successCriteriaModel.js";
import { storeToRefs } from "pinia";
import useId from 'quasar/src/composables/use-id/use-id.js';
import { useLearningObjectivesStore } from "store/modules/learningObjectivesStore";
import { computed, reactive, ref, toRefs } from "vue";
import { clusterManager } from "./clusterManager";
import {
  DEFAULT_CLUSTER_LEARNING_OBJECTIVE,
  DEFAULT_SUCCESS_CRITERIA,
} from "./learningObjectivesConstants";

export const createClusterLearningObjective = (_learningObjective = {}) => {
  // Cluster learning objective refs
  const learningObjective = reactive(
    structuredClone(DEFAULT_CLUSTER_LEARNING_OBJECTIVE),
  );
  const isInitialized = ref(false);

  /**
   * This will create a success criteria model and add it to the learning objective
   * @param {any} successCriteria
   * @returns {any}
   */
  const addSuccessCriteriaModels = (successCriteria) => {
    if (learningObjective?.success_criterias === undefined) {
      // Add success_criteria key to learning objective
      learningObjective.success_criterias = [];
    }

    if (Array.isArray(successCriteria)) {
      return successCriteria.forEach((criteria) => {
        learningObjective.success_criterias.push(
          Object.assign({}, DEFAULT_SUCCESS_CRITERIA, criteria),
        );
      });
    }

    return learningObjective.success_criterias.push(
      Object.assign({}, DEFAULT_SUCCESS_CRITERIA, successCriteria),
    );
  };

  // Initialize the learning objective
  (() => {
    const { success_criterias, ...learningObjectiveWithoutSuccessCriteria } =
      _learningObjective;

    Object.assign(learningObjective, learningObjectiveWithoutSuccessCriteria);

    // A stable client-side key. Attached cluster objectives and raw library
    // objectives are keyed off different tables, so their `id`s can collide;
    // namespacing keeps list keys / drag-reorder / removal unambiguous.
    if (!learningObjective.client_key) {
      learningObjective.client_key = `clo-${learningObjective.id}`;
    }

    if (success_criterias) {
      addSuccessCriteriaModels(success_criterias);
    }

    isInitialized.value = true;
  })();

  // Store
  const learningObjectivesStore = useLearningObjectivesStore();
  const {
    deleteLearningObjective,
    removeSuccessCriteriaFromLearningObjective,
    updateSuccessCriteria: useUpdateSuccessCriteria,
  } = learningObjectivesStore;
  const { libraryLearningObjectives } = storeToRefs(learningObjectivesStore);

  const hasSuccessCriteria = computed(
    () => !!learningObjective.success_criterias.length,
  );

  const clusterDetails = computed(() => {
    return [
      {
        icon: "fa-duotone fa-check",
        title: learningObjective.success_criterias.length
          ? learningObjective.success_criterias.length > 1
            ? `${learningObjective.success_criterias.length} succescriteria`
            : `${learningObjective.success_criterias.length} succescriterium`
          : "(nog) geen succescriteria",
      },
      {
        icon: "fa-duotone fa-list-ol",
        title: learningObjective.education_year_levels
          ? learningObjective.education_year_levels.join(", ")
          : "Geen jaarlagen",
      },
    ];
  });

  const parentCluster = computed(() => {
    const cluster = clusterManager.value.findClusterById(
      learningObjective.cluster_id,
    );

    if (!cluster) {
      throw new Error("Cluster not found");
    }

    return cluster;
  });

  const currentId = computed(() => learningObjective.id);

  const upsert = async ({ onUploadProgress } = {}) => {
    const { id = null } = learningObjective || {};

    const storedLearningObjective =
      id !== null
        ? await update({ onUploadProgress })
        : await add({ onUploadProgress });

    if (!storedLearningObjective.id) {
      throw new Error("No id found in storedLearningObjective");
    }

    // Merge parent-LO server state into the local model BEFORE iterating
    // success criteria. If any SC upsert throws below, the local LO's `id`
    // is still set so a retry routes through update() instead of add() —
    // avoiding a duplicate parent row server-side.
    // eslint-disable-next-line no-unused-vars
    const { success_criterias, ...rest } = storedLearningObjective;
    Object.assign(learningObjective, rest);

    const foundLibrarayLearningObjective = libraryLearningObjectives.value.find(
      ({ id }) => id === storedLearningObjective.id,
    );

    if (foundLibrarayLearningObjective) {
      Object.assign(foundLibrarayLearningObjective, storedLearningObjective);
    }

    for (const successCriteria of learningObjective.success_criterias) {
      if (successCriteria.is_temporary) {
        successCriteria.library_learning_objective_id =
          storedLearningObjective.id;
        await successCriteria.upsert(undefined, { onUploadProgress });
      }
    }

    return learningObjective;
  };

  const fetch = async () => {
    const { schoolId } = useHelpers();

    const { data } = await apiService.get(
      `/api/school/${schoolId}/library-learning-objectives/${learningObjective.id}`,
    );

    Object.assign(learningObjective, data.data);

    learningObjective.success_criterias = data.data.success_criterias.map(
      (criteria) => {
        const successCriteriaModel = new SuccessCriteria(criteria);
        successCriteriaModel.onDelete((successCriteria) => {
          _remove(learningObjective.success_criterias, ({ id }) => {
            return id === successCriteria.id;
          });
        });
        return successCriteriaModel;
      },
    );
  };

  const add = async ({ onUploadProgress } = {}) => {
    const { schoolId, rewriteModelToFormData } = useHelpers();

    const formData = rewriteModelToFormData(learningObjective);

    const {
      data: { data: storedLearningObjective },
    } = await apiService.post(
      `/api/school/${schoolId}/library-learning-objectives`,
      formData,
      { onUploadProgress: toAxiosUploadProgress(onUploadProgress) },
    );

    const learningObjectivesStore = useLearningObjectivesStore();
    const { libraryLearningObjectives } = storeToRefs(learningObjectivesStore);

    libraryLearningObjectives.value.push(storedLearningObjective);

    return storedLearningObjective;
  };

  const update = async ({ onUploadProgress } = {}) => {
    const { schoolId, rewriteModelToFormData } = useHelpers();

    const formData = rewriteModelToFormData(learningObjective);
    formData.set("_method", "PUT");

    const {
      data: { data: storedLearningObjective },
    } = await apiService.post(
      `/api/school/${schoolId}/library-learning-objectives/${learningObjective.id}`,
      formData,
      { onUploadProgress: toAxiosUploadProgress(onUploadProgress) },
    );

    return storedLearningObjective;
  };

  const addSuccessCriteria = async (successCriteria) => {
    const successCriteriaModel = new SuccessCriteria(successCriteria);

    if (!successCriteriaModel.id) {
      // Set a temporary id for the sort
      successCriteriaModel.id = useId().value;
      successCriteriaModel.library_learning_objective_id = learningObjective.id;
    }

    if (learningObjective.id) {
      const addedSuccessCriteria = await successCriteriaModel.add();
      learningObjective.success_criterias.push(addedSuccessCriteria);
    } else {
      learningObjective.success_criterias.push(successCriteriaModel);
    }

    successCriteriaModel.onDelete((successCriteria) => {
      _remove(learningObjective.success_criterias, ({ id }) => {
        return id === successCriteria.id;
      });
    });
  };

  const removeSuccessCriteria = async (successCriteria) => {
    try {
      const { id, cluster_id } = learningObjective;
      const { id: successCriteriaId } = successCriteria;

      // Remove the success criteria
      await removeSuccessCriteriaFromLearningObjective({
        learningObjectiveId: id,
        successCriteriaId,
        clusterId: cluster_id,
      });

      // Remove the success criteria from the learning objective
      _remove(learningObjective.success_criterias, ({ id: _id }) => {
        return _id === successCriteriaId;
      });

      // Re-index the success criteria
      updateOrderByIndex(learningObjective.success_criterias);
    } catch (error) {
      console.log("🚀 ~ removeSuccessCriteria ~ error:", error);
    }
  };

  const updateSuccessCriteria = async (successCriteria) => {
    try {
      const { id, cluster_id } = learningObjective;

      // Update the success criteria
      const { data } = await useUpdateSuccessCriteria({
        learningObjectiveId: id,
        clusterId: cluster_id,
        successCriteria,
      });

      const foundSuccessCriteria = learningObjective.success_criterias.find(
        ({ id }) => id === successCriteria.id,
      );

      if (foundSuccessCriteria === undefined) {
        throw new Error("Success criteria not found");
      }

      // Update the local success criteria
      Object.assign(foundSuccessCriteria, data);
    } catch (error) {
      throw new Error(error);
    }
  };

  const updateOrderByIndex = (model) => {
    // Update the order property based on the new position
    model.forEach((item, index) => {
      item.order = index;
    });
  };

  const remove = async () => {
    try {
      await deleteLearningObjective(learningObjective);

      parentCluster.value.removeLearningObjectiveFromCluster(currentId.value);
    } catch (error) {
      console.log("🚀 ~ remove ~ error:", error);
    }
  };

  const setEditingState = (state) => {
    learningObjective.is_editing = state;
  };

  const searchTopics = async (value) => {
    const { schoolId } = useHelpers();
    try {
      const { data } = await apiService.get(
        `api/school/${schoolId}/topics/search`,
        {
          params: {
            search: value,
          },
        },
      );

      return data;
    } catch (e) {
      throw new Error(e);
    }
  };

  const isEditingSuccessCriteria = computed(() =>
    learningObjective.success_criterias.some(({ is_editing }) => is_editing),
  );

  return {
    isInitialized,
    hasSuccessCriteria,
    clusterDetails,
    isEditingSuccessCriteria,
    update,
    remove,
    addSuccessCriteria,
    removeSuccessCriteria,
    setEditingState,
    updateSuccessCriteria,
    upsert,
    searchTopics,
    fetch,
    ...toRefs(learningObjective),
  };
};
